"use client";

import Image from "next/image";
import { type ReactNode, type SVGProps } from "react";
import { customerSupportPhone, customerSupportTelHref, isSiteActive } from "@/shared/config/customerSupport";
import styles from "./CorrectedLandingContent.module.scss";
import {
  CorrectedAppCta,
  CorrectedFaq,
  CorrectedHero,
  RevealSection,
} from "./CorrectedSharedSections";

export { CorrectedAppCta, CorrectedFaq, CorrectedHero };
export type { CorrectedHeroProps, HeroAction } from "./CorrectedSharedSections";

const ASSET_ROOT = "/assets/corrected/landing";
const IMPACT_DIVIDER = "/assets/shared/impact-wave-f87a3454-2945-41c5-9595-4b931e8a9a08.svg";

const impactStats = [
  {
    icon: `${ASSET_ROOT}/impact-families-160.webp`,
    value: "5000+",
    title: "Families Supported",
    description: "Happy families receive compassionate nursing care.",
  },
  {
    icon: `${ASSET_ROOT}/impact-nurses.webp`,
    value: "150+",
    title: "Verified Nurses",
    description: "Identity, qualification, and background verified.",
  },
  {
    icon: `${ASSET_ROOT}/impact-visits.webp`,
    value: "2,500+",
    title: "Home Visits",
    description: "Professional nursing care delivered safely at home.",
  },
  {
    icon: `${ASSET_ROOT}/impact-satisfaction.webp`,
    value: "98%",
    title: "Family Satisfaction",
    description: "Built through trust, compassion, and quality care.",
  },
  {
    icon: `${ASSET_ROOT}/impact-support.webp`,
    value: "24/7",
    title: "Care Support",
    description: "Always ready to guide families whenever needed.",
  },
] as const;

const benefits = [
  {
    image: `${ASSET_ROOT}/why-verified.webp`,
    title: "Verified Home Nurses",
    description: "Every nurse is verified, qualified, and background checked for your safety.",
  },
  {
    image: `${ASSET_ROOT}/why-professional.webp`,
    title: "Professional Care at Home",
    description: "Expert nursing care delivered safely in the comfort of your home.",
  },
  {
    image: `${ASSET_ROOT}/why-coordination.webp`,
    title: "Dedicated Care Coordination",
    description: "Our care team supports scheduling and answers your questions.",
  },
  {
    image: `${ASSET_ROOT}/why-personalized.webp`,
    title: "Personalized Care Plans",
    description: "Care tailored to your health needs, recovery, and daily routine.",
  },
  {
    image: `${ASSET_ROOT}/why-updates.webp`,
    title: "Real-Time Family Updates",
    description: "Get real-time visit updates via WhatsApp after every visit.",
  },
  {
    image: `${ASSET_ROOT}/why-updates.webp`,
    title: "Trusted & Transparent",
    description: "Clear communication, reliable care, and transparent pricing with no hidden surprises.",
  },
] as const;

const careStandards = [
  { title: "Transparent Pricing", description: "Know exactly what you're paying before your service begins." },
  { title: "No Hidden Charges", description: "No unexpected bills or last-minute fees." },
  { title: "No Long Term Commitment", description: "Book once or schedule ongoing care—you're always in control." },
  { title: "Pay Only for the Care You Receive", description: "Flexible billing based on the services actually delivered." },
  { title: "Clear Service Documentation", description: "Every visit is documented and shared with complete transparency." },
] as const;

const trustChecklist = [
  "Verified & Background-Checked Care Professionals",
  "Personalized Care Plans Tailored To Individual Needs",
  "Real-Time Visit Updates & Care Reports",
  "Transparent, Secure & Reliable Care Delivery",
  "Dedicated Care Coordination Team Available 24/7",
] as const;

type IconProps = SVGProps<SVGSVGElement>;

function HeartIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
    </svg>
  );
}

function PhoneIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="M5.3 3.8h3l1.5 4.4-2 1.7a14.8 14.8 0 0 0 6.3 6.3l1.7-2 4.4 1.5v3a1.5 1.5 0 0 1-1.5 1.5C10.5 20.2 3.8 13.5 3.8 5.3a1.5 1.5 0 0 1 1.5-1.5Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function CalendarIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <rect height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" width="18" x="3" y="5" />
      <path d="M7 3v4m10-4v4M3 10h18m-12 5 2 2 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function CheckIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" {...props}>
      <path d="m5 12.5 4.2 4.2L19 6.9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" />
    </svg>
  );
}

function LockIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 32 40" {...props}>
      <rect height="22" rx="5" stroke="currentColor" strokeWidth="2.2" width="26" x="3" y="15" />
      <path d="M9 15V9a7 7 0 0 1 14 0v6M16 24v5" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
    </svg>
  );
}

function ShieldIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 40 44" {...props}>
      <path d="M20 2.5 35 8v11.4c0 9.6-6.1 17.5-15 21.8C11.1 36.9 5 29 5 19.4V8l15-5.5Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2.4" />
      <path d="m13.5 21 4.2 4.2 8.8-9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
    </svg>
  );
}

function CareIcon(props: IconProps) {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 32 32" {...props}>
      <path d="M16 28a12 12 0 0 0 10.8-17.2M16 28A12 12 0 0 1 5.2 10.8M16 28v-7" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      <path d="M11 14c0-2.2 1.8-4 4-4 .7 0 1.4.2 2 .6a4 4 0 0 1 6.3 3.3c0 3-3 5.2-6.3 7.6-3.3-2.4-6-4.6-6-7.5Z" fill="currentColor" />
    </svg>
  );
}

function CorrectedImpact() {
  return (
    <RevealSection className={styles.impactSection} id="impact">
      <div className={`${styles.container} ${styles.navAligned} container`}>
        <header className={styles.sectionHeading}>
          <p>carevinco impact</p>
          <h2>
            More Than Numbers. More <span>Human Care.</span>
          </h2>
          <small>Every number represents a family supported, a nurse verified, and a promise delivered with compassion.</small>
        </header>
        <div className={`${styles.impactGrid} ${styles.stagger}`}>
          {impactStats.map((stat) => (
            <article data-reveal-item key={stat.title}>
              <span className={styles.impactIcon}>
                <Image alt="" fill sizes="100px" src={stat.icon} />
              </span>
              <strong>{stat.value}</strong>
              <h3>{stat.title}</h3>
              <p>{stat.description}</p>
            </article>
          ))}
        </div>
      </div>
      <div aria-hidden="true" className={styles.impactDivider}>
        <Image alt="" height={74} sizes="100vw" src={IMPACT_DIVIDER} width={1920} />
      </div>
    </RevealSection>
  );
}

function BenefitItem({ benefit }: { benefit: (typeof benefits)[number] }) {
  return (
    <article className={styles.benefitItem} data-reveal-item>
      <Image alt="" height={83} src={benefit.image} width={83} />
      <div>
        <h3>{benefit.title}</h3>
        <p>{benefit.description}</p>
      </div>
    </article>
  );
}

function CorrectedWhy() {
  return (
    <RevealSection className={styles.whySection} id="why">
      <div className={`${styles.wideContainer} ${styles.navAligned} container`}>
        <header className={styles.whyHeading}>
          <p>
            <HeartIcon /> why families choose carevinco
          </p>
          <h2>
            <span>Trusted </span>Home Nursing, Built Around Your Family
          </h2>
          <small>
            Professional home nursing with compassionate care, verified nurses, and complete transparency—helping families feel confident and supported every day.
          </small>
          <button className={`${styles.button} ${styles.buttonPrimary} ${styles.whyButton}`} data-open-inquiry="true" type="button">
            <CalendarIcon />
            Book a Care Visit
          </button>
          <div className={styles.trustBadges}>
            <span><LockIcon /> Secure &amp; Private</span>
            <span><ShieldIcon /> 100% Trusted Providers</span>
          </div>
        </header>

        <div className={styles.benefitsLayout}>
          <div className={`${styles.benefitColumn} ${styles.benefitColumnLeft} ${styles.stagger}`}>
            {benefits.slice(0, 3).map((benefit) => <BenefitItem benefit={benefit} key={benefit.title} />)}
          </div>
          <div className={styles.whyVisual} data-reveal-item>
            <Image alt="Caregiver supporting an older adult at home" fill sizes="(max-width: 767px) 90vw, (max-width: 1023px) 55vw, 32vw" src={`${ASSET_ROOT}/why-center-optimized.webp`} />
          </div>
          <div className={`${styles.benefitColumn} ${styles.benefitColumnRight} ${styles.stagger}`}>
            {benefits.slice(3).map((benefit) => <BenefitItem benefit={benefit} key={benefit.title} />)}
          </div>
        </div>
      </div>
    </RevealSection>
  );
}

function CorrectedCareStandards() {
  return (
    <RevealSection className={styles.standardsSection} id="standards">
      <div className={`${styles.standardsLayout} ${styles.stagger} ${styles.navAligned} container`}>
        <div className={styles.standardsCopy} data-reveal-item>
          <header>
            <p>the professionals behind every visit</p>
            <h2>
              Carevinco Care <span>Standards</span>
            </h2>
          </header>
          <p>Every home visit is delivered by trained, verified professionals following standardized care protocols.</p>
          <ul>
            {careStandards.map((standard) => (
              <li key={standard.title}>
                <span className={styles.checkCircle}><CheckIcon /></span>
                <div>
                  <strong>{standard.title}</strong>
                  <p>{standard.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.standardsImage} data-reveal-item>
          <Image alt="Care professional providing compassionate home nursing" fill sizes="(max-width: 1023px) calc(100vw - 32px), 50vw" src={`${ASSET_ROOT}/standards-care.webp`} />
        </div>
      </div>
    </RevealSection>
  );
}

function MiniTitle({ children }: { children: ReactNode }) {
  return (
    <div className={styles.miniTitle}>
      <CareIcon />
      <span>{children}</span>
    </div>
  );
}

function CorrectedTrust() {
  return (
    <RevealSection className={styles.trustSection} id="trust">
      <div className={`${styles.container} ${styles.navAligned} container`}>
        <header className={styles.sectionHeading}>
          <p>Families Trust Carevinco</p>
          <h2>
            Professional Care. <span>Proven</span> Every Day.
          </h2>
          <small>Every visit follows a consistent standard of safety, compassion, and professional care—so your family always knows what to expect.</small>
        </header>

        <div className={`${styles.trustGrid} ${styles.stagger}`}>
          <article className={styles.storyColumn} data-reveal-item>
            <div>
              <MiniTitle>Families Love Carevinco</MiniTitle>
              <h3>Real Stories. Real Impact.</h3>
            </div>
            <blockquote>
              <span aria-hidden="true">&rdquo;</span>
              <p>Carevinco helped us find the right nurse for my mother. The updates and transparency gave our family complete peace of mind.&quot;</p>
              <footer>— Priya S.</footer>
            </blockquote>
          </article>
          <div aria-hidden="true" className={styles.verticalRule} />
          <article className={styles.checklistColumn} data-reveal-item>
            <MiniTitle>Families Trust Carevinco</MiniTitle>
            <ul>
              {trustChecklist.map((item) => (
                <li key={item}><CheckIcon /> {item}</li>
              ))}
            </ul>
            <a href="#impact">Learn More About Carevinco →</a>
          </article>
          <div aria-hidden="true" className={styles.verticalRule} />
          <article className={styles.consultColumn} data-reveal-item>
            <div>
              <MiniTitle>Your loved ones deserve trusted care.</MiniTitle>
              <h3>Ready To Bring Peace Of Mind Home?</h3>
              <p>From elder care and home nursing to personalized support and wellness services, Carevinco helps families stay connected, informed, and confident.</p>
            </div>
            <div>
              <strong>Request A Free Consultation</strong>
              {isSiteActive ? (
                <a className={`${styles.button} ${styles.buttonOrange} ${styles.consultButton}`} href={customerSupportTelHref}>
                  <PhoneIcon />
                  Call Our Care Team : {customerSupportPhone}
                </a>
              ) : null}
            </div>
          </article>
        </div>
      </div>
    </RevealSection>
  );
}

export default function CorrectedLandingContent() {
  return (
    <main className={styles.landing}>
      <CorrectedHero />
      <CorrectedImpact />
      <CorrectedWhy />
      <CorrectedCareStandards />
      <CorrectedTrust />
      <CorrectedAppCta />
      <CorrectedFaq />
    </main>
  );
}
